import React from 'react';
import { ReportSnapshotSummary } from '../../services/reportsService';
import { parseReportDate } from '../../utils/snapshotDate';
import './SnapshotCard.css';

interface SnapshotCardProps {
  snapshot: ReportSnapshotSummary;
  onCardClick: (snapshot: ReportSnapshotSummary) => void;
}

/**
 * SnapshotCard - Displays a summary of a single weekly snapshot
 * Shows: date, compliance rate, API counts (compliant/partial/non-compliant)
 */
export const SnapshotCard: React.FC<SnapshotCardProps> = ({ snapshot, onCardClick }) => {
  const formatDate = (dateStr: string): string => {
    const date = parseReportDate(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getComplianceColor = (rate: number): string => {
    if (rate >= 80) return 'compliant';
    if (rate >= 50) return 'partial';
    return 'non-compliant';
  };

  const complianceColor = getComplianceColor(snapshot.overallComplianceRate);

  return (
    <div
      className={`snapshot-card snapshot-card-${complianceColor}`}
      onClick={() => onCardClick(snapshot)}
      role="button"
      tabIndex={0}
      onKeyPress={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onCardClick(snapshot);
        }
      }}
    >
      <div className="snapshot-card-header">
        <h3 className="snapshot-card-date">{formatDate(snapshot.reportDate)}</h3>
        <span
          className={`snapshot-card-rate snapshot-card-rate-${complianceColor}`}
          title="Overall compliance rate"
        >
          {snapshot.overallComplianceRate.toFixed(1)}%
        </span>
      </div>

      <div className="snapshot-card-total">
        <span className="snapshot-card-stat-label">Total APIs</span>
        <span className="snapshot-card-total-value">{snapshot.totalApisMonitored}</span>
      </div>

      <div className="snapshot-card-stats">
        <div className="snapshot-card-stat">
          <span className="snapshot-card-stat-label">Compliant</span>
          <span className="snapshot-card-stat-value compliant">
            {snapshot.complianceStats.compliant}
          </span>
        </div>
        <div className="snapshot-card-stat">
          <span className="snapshot-card-stat-label">Partial</span>
          <span className="snapshot-card-stat-value partial">
            {snapshot.complianceStats.partiallyCompliant}
          </span>
        </div>
        <div className="snapshot-card-stat">
          <span className="snapshot-card-stat-label">Non-Compliant</span>
          <span className="snapshot-card-stat-value non-compliant">
            {snapshot.complianceStats.nonCompliant}
          </span>
        </div>
      </div>

      <div className="snapshot-card-footer">
        <div className="findings-info">
          <span className="findings-label">Findings:</span>
          <span className="findings-value">{snapshot.totalFindingsCount}</span>
          <span className="findings-detail">
            ({snapshot.averageFindingsPerApi.toFixed(1)} avg/API)
          </span>
        </div>
        <span className="view-details">View →</span>
      </div>
    </div>
  );
};

export default SnapshotCard;
